import { NO_MOVE, type MoveInput } from "../game/kitchen";

// Everything the player does with keys, mouse and fingers, boiled down to a
// frame's `MoveInput` and a Use press. Desktop: WASD / arrows to walk (left
// and right arrows turn), the mouse to look (pointer lock after the first
// click, or drag to look where the page can't lock it), E, Space, Enter or a
// click to use. Touch: `TouchControls` writes the stick and look drags here.

const MOUSE_SENSITIVITY = 0.0028;
const DRAG_SENSITIVITY = 0.006;

export class Controls {
  private readonly keys = new Set<string>();
  private lookYaw = 0;
  private lookPitch = 0;
  private usePresses = 0;
  private useHeldByKey = false;
  private dragging = false;
  private dragMoved = 0;
  /** False once the page has refused to lock the mouse: then drag to look. */
  private canLock = true;
  /** The touch stick, x right and y forward, each in [-1, 1]. */
  stick = { x: 0, y: 0 };
  /** Whether the on-screen Use button is held down. */
  touchUseHeld = false;
  /** Set by the engine: input only counts while the shift runs. */
  enabled = false;
  /** From the settings: look speed multiplier, and up/down swapped. */
  lookSpeed = 1;
  invertY = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly onUnlock: () => void,
  ) {
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("blur", this.clear);
    canvas.addEventListener("mousedown", this.onMouseDown);
    window.addEventListener("mouseup", this.onMouseUp);
    window.addEventListener("mousemove", this.onMouseMove);
    document.addEventListener("pointerlockchange", this.onLockChange);
  }

  dispose(): void {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("blur", this.clear);
    this.canvas.removeEventListener("mousedown", this.onMouseDown);
    window.removeEventListener("mouseup", this.onMouseUp);
    window.removeEventListener("mousemove", this.onMouseMove);
    document.removeEventListener("pointerlockchange", this.onLockChange);
    this.unlock();
  }

  /** Adds a look drag from touch, in screen pixels. */
  touchLook(dx: number, dy: number): void {
    if (!this.enabled) return;
    this.lookYaw -= dx * DRAG_SENSITIVITY * this.lookSpeed;
    this.lookPitch -= dy * DRAG_SENSITIVITY * this.lookSpeed * (this.invertY ? -1 : 1);
  }

  /** A tap of the on-screen Use button. */
  pressUse(): void {
    if (this.enabled) this.usePresses++;
  }

  /** This frame's movement; look deltas are used up. */
  read(): MoveInput {
    if (!this.enabled) {
      this.lookYaw = this.lookPitch = 0;
      return NO_MOVE;
    }
    const k = (...names: string[]) => (names.some((n) => this.keys.has(n)) ? 1 : 0);
    const input: MoveInput = {
      forward: k("KeyW", "ArrowUp") - k("KeyS", "ArrowDown") + this.stick.y,
      strafe: k("KeyD") - k("KeyA") + this.stick.x,
      turn: k("ArrowLeft", "KeyQ") - k("ArrowRight"),
      lookYaw: this.lookYaw,
      lookPitch: this.lookPitch,
    };
    this.lookYaw = this.lookPitch = 0;
    return input;
  }

  /** Use presses since the last call. */
  takeUses(): number {
    const n = this.usePresses;
    this.usePresses = 0;
    return n;
  }

  /** Whether Use is being held (for scrubbing by holding it down). */
  get useHeld(): boolean {
    return this.enabled && (this.useHeldByKey || this.touchUseHeld || (this.dragging && (this.locked || !this.canLock)));
  }

  get locked(): boolean {
    return document.pointerLockElement === this.canvas;
  }

  unlock(): void {
    if (this.locked) document.exitPointerLock();
  }

  /** Takes the mouse for looking (from a click: Clock in, Back to work). */
  lock(): void {
    if (!this.canLock || this.locked || !this.canvas.requestPointerLock) return;
    try {
      const asked = this.canvas.requestPointerLock() as unknown;
      if (asked instanceof Promise) asked.catch(() => (this.canLock = false));
    } catch {
      this.canLock = false;
    }
  }

  /** On a desktop that can lock the mouse but hasn't yet: click to look around. */
  get needsClick(): boolean {
    return this.enabled && this.canLock && !this.locked;
  }

  private readonly onKeyDown = (e: KeyboardEvent) => {
    if (!this.enabled || e.metaKey || e.ctrlKey || e.altKey) return;
    if (["KeyE", "Space", "Enter", "NumpadEnter"].includes(e.code)) {
      e.preventDefault();
      if (!e.repeat) this.usePresses++;
      this.useHeldByKey = true;
      return;
    }
    if (/^(Key[WASDQ]|Arrow)/.test(e.code)) {
      e.preventDefault();
      this.keys.add(e.code);
    }
  };

  private readonly onKeyUp = (e: KeyboardEvent) => {
    this.keys.delete(e.code);
    if (["KeyE", "Space", "Enter", "NumpadEnter"].includes(e.code)) this.useHeldByKey = false;
  };

  private readonly clear = () => {
    this.keys.clear();
    this.useHeldByKey = false;
    this.dragging = false;
  };

  private readonly onMouseDown = (e: MouseEvent) => {
    if (!this.enabled || e.button !== 0) return;
    this.dragging = true;
    this.dragMoved = 0;
    if (this.locked) {
      this.usePresses++;
      return;
    }
    // The first click takes the mouse for looking. Where the page can't lock
    // it (some embeds refuse), dragging looks instead and a click is a Use.
    this.lock();
  };

  private readonly onMouseUp = (e: MouseEvent) => {
    if (e.button !== 0) return;
    if (this.dragging && this.enabled && !this.canLock && this.dragMoved < 6) this.usePresses++;
    this.dragging = false;
  };

  private readonly onMouseMove = (e: MouseEvent) => {
    if (!this.enabled) return;
    if (this.locked) {
      this.lookYaw -= e.movementX * MOUSE_SENSITIVITY * this.lookSpeed;
      this.lookPitch -= e.movementY * MOUSE_SENSITIVITY * this.lookSpeed * (this.invertY ? -1 : 1);
    } else if (this.dragging && !this.canLock) {
      this.dragMoved += Math.abs(e.movementX) + Math.abs(e.movementY);
      this.lookYaw -= e.movementX * DRAG_SENSITIVITY * 0.6 * this.lookSpeed;
      this.lookPitch -= e.movementY * DRAG_SENSITIVITY * 0.6 * this.lookSpeed * (this.invertY ? -1 : 1);
    }
  };

  private readonly onLockChange = () => {
    if (this.locked) return;
    this.dragging = false;
    // Esc gives the mouse back; treat it as a pause, like any PC game.
    if (this.enabled) this.onUnlock();
  };
}
